/**
 * Componente: PurchaseDetailModal
 * 
 * Modal que muestra los detalles completos de una compra:
 * - Información general (orden, fecha, plan, estado)
 * - Detalles de evaluaciones
 * - Información de pago
 * - Datos de facturación
 * - Botón para descargar factura
 * 
 * @param {boolean} open - Si el modal está abierto
 * @param {Function} onClose - Callback para cerrar el modal
 * @param {Object} purchase - Datos de la compra
 * @param {Function} onDownloadInvoice - Callback para descargar factura
 * @param {boolean} loading - Estado de carga de detalles
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  CircularProgress,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import Icon from '@mui/material/Icon';
import PurchaseStatusBadge from './PurchaseStatusBadge';
import {
  formatCurrency,
  formatDate,
  getTypeConfig,
} from 'services/purchaseService';

function PurchaseDetailModal({ open, onClose, purchase, onDownloadInvoice, loading }) {
  if (!purchase && !loading) {
    return null;
  }

  const handleDownload = () => {
    if (purchase?.invoice?.number) {
      onDownloadInvoice(purchase.invoice.number);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox display="flex" alignItems="center" gap={2}>
            <Icon fontSize="large" color="info">
              receipt_long
            </Icon>
            <MDTypography variant="h5" fontWeight="medium">
              Detalles de Compra
            </MDTypography>
          </MDBox>
          <MDButton
            variant="text"
            color="secondary"
            iconOnly
            onClick={onClose}
          >
            <Icon>close</Icon>
          </MDButton>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {loading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" py={5}>
            <CircularProgress color="info" />
          </MDBox>
        ) : (
          <MDBox>
            {/* Información General */}
            <MDBox mb={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Información General
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <MDBox>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      Número de Orden
                    </MDTypography>
                    <MDTypography variant="button" display="block">
                      {purchase.orderNumber}
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDBox>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      Fecha de Compra
                    </MDTypography>
                    <MDTypography variant="button" display="block">
                      {formatDate(purchase.date, true)}
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDBox>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      Plan
                    </MDTypography>
                    <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
                      <Icon fontSize="small">{getTypeConfig(purchase.type).icon}</Icon>
                      <MDTypography variant="button">
                        {getTypeConfig(purchase.type).label}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDBox>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      Estado
                    </MDTypography>
                    <MDBox mt={0.5}>
                      <PurchaseStatusBadge status={purchase.status} size="medium" />
                    </MDBox>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>

            <Divider />

            {/* Detalles de Licencias y Pago */}
            <MDBox my={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Detalles de la Compra
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <MDBox>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      Evaluaciones Adquiridas
                    </MDTypography>
                    <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
                      <Icon fontSize="small" color="info">
                        workspace_premium
                      </Icon>
                      <MDTypography variant="h6" color="info">
                        {purchase.licensesCount}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDBox>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      Monto Total
                    </MDTypography>
                    <MDTypography variant="h6" color="success" mt={0.5}>
                      {formatCurrency(purchase.amount, purchase.currency)}
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12}>
                  <MDBox>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      Descripción
                    </MDTypography>
                    <MDTypography variant="button" display="block" mt={0.5}>
                      {purchase.description}
                    </MDTypography>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>

            <Divider />

            {/* Información de Pago */}
            <MDBox my={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Información de Pago
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <MDBox>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      Método de Pago
                    </MDTypography>
                    <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
                      <Icon fontSize="small">credit_card</Icon>
                      <MDTypography variant="button">
                        Tarjeta •••• {purchase.lastFourDigits}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Grid>
                {purchase.invoice && (
                  <Grid item xs={12} sm={6}>
                    <MDBox>
                      <MDTypography variant="caption" color="text" fontWeight="bold">
                        Número de Factura
                      </MDTypography>
                      <MDTypography variant="button" display="block" mt={0.5}>
                        {purchase.invoice.number}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                )}
              </Grid>
            </MDBox>

            <Divider />

            {/* Información de Facturación */}
            {purchase.billingInfo && (
              <MDBox mt={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Información de Facturación
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <MDBox>
                      <MDTypography variant="caption" color="text" fontWeight="bold">
                        Razón Social
                      </MDTypography>
                      <MDTypography variant="button" display="block">
                        {purchase.billingInfo.name}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDBox>
                      <MDTypography variant="caption" color="text" fontWeight="bold">
                        RFC
                      </MDTypography>
                      <MDTypography variant="button" display="block">
                        {purchase.billingInfo.rfc}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDBox>
                      <MDTypography variant="caption" color="text" fontWeight="bold">
                        Código Postal
                      </MDTypography>
                      <MDTypography variant="button" display="block">
                        {purchase.billingInfo.zipCode}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12}>
                    <MDBox>
                      <MDTypography variant="caption" color="text" fontWeight="bold">
                        Dirección
                      </MDTypography>
                      <MDTypography variant="button" display="block">
                        {purchase.billingInfo.address}, {purchase.billingInfo.city}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            )}
          </MDBox>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={onClose}
        >
          Cerrar
        </MDButton>
        {purchase?.invoice && (
          <MDButton
            variant="gradient"
            color="success"
            onClick={handleDownload}
            startIcon={<Icon>download</Icon>}
          >
            Descargar Factura
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

PurchaseDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  purchase: PropTypes.shape({
    id: PropTypes.string,
    orderNumber: PropTypes.string,
    date: PropTypes.string,
    type: PropTypes.string,
    status: PropTypes.string,
    amount: PropTypes.number,
    currency: PropTypes.string,
    licensesCount: PropTypes.number,
    paymentMethod: PropTypes.string,
    lastFourDigits: PropTypes.string,
    description: PropTypes.string,
    invoice: PropTypes.shape({
      number: PropTypes.string,
      downloadUrl: PropTypes.string,
    }),
    billingInfo: PropTypes.shape({
      name: PropTypes.string,
      rfc: PropTypes.string,
      address: PropTypes.string,
      city: PropTypes.string,
      zipCode: PropTypes.string,
    }),
  }),
  onDownloadInvoice: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

PurchaseDetailModal.defaultProps = {
  purchase: null,
  loading: false,
};

export default PurchaseDetailModal;
